/**
 * PRD.305 — Laboratory write path.
 *
 * Every laboratory mutation in the app goes through here, so the Laboratory
 * module, Patient Workspace Labs and Consultation Labs cannot drift into three
 * different write behaviours. Each function consults the pure guards in
 * `@helios/data-access` before writing and throws if the transition is not
 * allowed — the repository's established shape (see patient-package-service.ts,
 * which calls `canTransition` the same way).
 *
 * Offline-first: all writes are `db.execute(...)` against the local PowerSync
 * SQLite, which is what produces the CRUD entries syncWrite later applies.
 * `db.writeTransaction(tx => tx.execute(...))` is deliberately NOT used — it
 * produces no CRUD entry and the write would never reach Postgres.
 *
 * Billing is not reimplemented: `billLabTests` reuses the same invoice /
 * invoice_items column set and settlement pass as Billing and PRD.288's
 * initial-fee service.
 */
import {
  canAdvanceLabTest,
  canCancelLabTest,
  canUploadLabResult,
  canPublishLabResult,
  canCorrectLabResult,
  labInvoiceItemId,
  LAB_TEST_INVOICE_SOURCE_TYPE,
  normalizeLabTestStatus,
  initialLabFulfillment,
  canAcceptLabRequest,
  canMarkLabRequestExternal,
  type LabTestStatus,
  type LabOrderTestDraft,
} from '@helios/data-access';
import {
  invoiceSummary,
  type InvoiceItem,
  type DiscountType,
} from '../pages/patient/consultation/consultation-data';
import { recomputeInvoiceItemPaymentStatus } from './invoice-item-settlement';

// PowerSyncDatabase — accepts any object with execute + getAll.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbLike = any;

/** Actor stamped onto workflow transitions. */
export interface LabActor {
  uid: string;
  name?: string | null;
}

// ─── Ordering ───

/**
 * One test being ordered. The shape lives in data-access alongside
 * `buildLabOrderTestDrafts`, so the builder and the writer cannot drift.
 */
export type LabTestDraft = LabOrderTestDraft;

export interface CreateLabOrderParams {
  db: DbLike;
  orgId: string;
  clinicId?: string | null;
  patientId: string;
  visitId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  orderingProviderId?: string | null;
  actor: LabActor;
  /** Which surface raised the order — feeds `lab_orders.source`. */
  source: 'consultation' | 'patient_workspace' | 'laboratory';
  tests: LabTestDraft[];
  notes?: string | null;
}

export interface CreateLabOrderResult {
  orderId: string;
  testIds: string[];
}

/**
 * Create an order and its tests. Every test starts at `ordered`; nothing is
 * billed here — billing is a separate, explicit step (`billLabTests`) because
 * PRD §4 allows an order to exist before payment wherever the clinic's own
 * billing rules do.
 */
export async function createLabOrder(params: CreateLabOrderParams): Promise<CreateLabOrderResult> {
  const { db, orgId, patientId, actor, tests } = params;
  if (tests.length === 0) throw new Error('A laboratory order needs at least one test.');

  const now = new Date().toISOString();
  const orderId = crypto.randomUUID();

  // Human-readable reference, derived the same way Billing derives invoice_no.
  const existing = (await db.getAll(`SELECT order_number FROM lab_orders WHERE org_id = ?`, [
    orgId,
  ])) as { order_number: string | null }[];
  const maxNum = existing.reduce((max: number, row) => {
    const n = parseInt((row.order_number ?? '').replace('LAB-', ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  const orderNumber = `LAB-${String(maxNum + 1).padStart(5, '0')}`;

  // PRD.305 — a consultation request is a recommendation the patient may take
  // to any laboratory, so it starts OUTSIDE the in-house work queue. Orders
  // raised by staff (Laboratory module / patient chart) start in-house and
  // behave exactly as they did before this distinction existed.
  const fulfillment = initialLabFulfillment(params.source);

  await db.execute(
    `INSERT INTO lab_orders
      (id, org_id, clinic_id, patient_id, visit_id, encounter_id, appointment_id, queue_id,
       ordering_provider_id, ordered_by, ordered_by_name, source, order_number, notes,
       fulfillment, fulfillment_at, fulfillment_by,
       ordered_at, cancelled_at, cancelled_by, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, NULL, ?, NULL, NULL, ?, ?)`,
    [
      orderId,
      orgId,
      params.clinicId ?? null,
      patientId,
      params.visitId ?? null,
      params.encounterId ?? null,
      params.appointmentId ?? null,
      params.queueId ?? null,
      params.orderingProviderId ?? null,
      actor.uid,
      actor.name ?? null,
      params.source,
      orderNumber,
      params.notes ?? null,
      fulfillment,
      now,
      now,
      now,
    ],
  );

  const testIds: string[] = [];
  for (const [index, draft] of tests.entries()) {
    const testId = crypto.randomUUID();
    testIds.push(testId);
    await db.execute(
      `INSERT INTO lab_order_tests
        (id, org_id, lab_order_id, patient_id, service_id, test_name, test_code_snapshot,
         price_cents_snapshot, status, sort_order,
         collected_at, collected_by, started_at, started_by,
         result_ready_at, result_ready_by, published_at, published_by,
         cancelled_at, cancelled_by, cancel_reason, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'ordered', ?,
               NULL, NULL, NULL, NULL,
               NULL, NULL, NULL, NULL,
               NULL, NULL, NULL, NULL, ?, ?)`,
      [
        testId,
        orgId,
        orderId,
        patientId,
        draft.serviceId,
        draft.name,
        draft.code ?? null,
        draft.priceCents ?? null,
        index,
        now,
        now,
      ],
    );
  }

  return { orderId, testIds };
}

// ─── Workflow transitions ───

const STATUS_STAMPS: Partial<Record<LabTestStatus, { at: string; by: string }>> = {
  collected: { at: 'collected_at', by: 'collected_by' },
  in_progress: { at: 'started_at', by: 'started_by' },
  result_ready: { at: 'result_ready_at', by: 'result_ready_by' },
};

/**
 * Move one test forward. Refuses anything the pure guard rejects, so a stale
 * UI (or a second device that already advanced this test) cannot rewind state.
 */
export async function advanceLabTestStatus(params: {
  db: DbLike;
  testId: string;
  to: LabTestStatus;
  actor: LabActor;
}): Promise<void> {
  const { db, testId, to, actor } = params;
  const rows = (await db.getAll(`SELECT status FROM lab_order_tests WHERE id = ? LIMIT 1`, [
    testId,
  ])) as { status: string | null }[];
  if (rows.length === 0) throw new Error('Laboratory test not found.');

  const verdict = canAdvanceLabTest({ status: rows[0].status }, to);
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This status change is not allowed.');

  const now = new Date().toISOString();
  const stamp = STATUS_STAMPS[to];
  if (stamp) {
    await db.execute(
      `UPDATE lab_order_tests
          SET status = ?, ${stamp.at} = COALESCE(${stamp.at}, ?), ${stamp.by} = COALESCE(${stamp.by}, ?), updated_at = ?
        WHERE id = ?`,
      [to, now, actor.uid, now, testId],
    );
    return;
  }
  await db.execute(`UPDATE lab_order_tests SET status = ?, updated_at = ? WHERE id = ?`, [
    to,
    now,
    testId,
  ]);
}

/** Cancel one test. Published work is a clinical record and is never cancelled. */
export async function cancelLabTest(params: {
  db: DbLike;
  testId: string;
  actor: LabActor;
  reason?: string | null;
}): Promise<void> {
  const { db, testId, actor } = params;
  const rows = (await db.getAll(`SELECT status FROM lab_order_tests WHERE id = ? LIMIT 1`, [
    testId,
  ])) as { status: string | null }[];
  if (rows.length === 0) throw new Error('Laboratory test not found.');

  const verdict = canCancelLabTest({ status: rows[0].status });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This test cannot be cancelled.');

  const now = new Date().toISOString();
  await db.execute(
    `UPDATE lab_order_tests
        SET status = 'cancelled', cancelled_at = ?, cancelled_by = ?, cancel_reason = ?, updated_at = ?
      WHERE id = ?`,
    [now, actor.uid, params.reason ?? null, now, testId],
  );
}

/** Cancel every still-cancellable test on an order, then stamp the order. */
export async function cancelLabOrder(params: {
  db: DbLike;
  orderId: string;
  actor: LabActor;
  reason?: string | null;
}): Promise<{ cancelled: number; skipped: number }> {
  const { db, orderId, actor } = params;
  const tests = (await db.getAll(`SELECT id, status FROM lab_order_tests WHERE lab_order_id = ?`, [
    orderId,
  ])) as { id: string; status: string | null }[];

  let cancelled = 0;
  let skipped = 0;
  for (const test of tests) {
    if (!canCancelLabTest({ status: test.status }).ok) {
      skipped += 1; // already published or already cancelled — left intact
      continue;
    }
    await cancelLabTest({ db, testId: test.id, actor, reason: params.reason });
    cancelled += 1;
  }

  const now = new Date().toISOString();
  await db.execute(
    `UPDATE lab_orders SET cancelled_at = ?, cancelled_by = ?, updated_at = ? WHERE id = ?`,
    [now, actor.uid, now, orderId],
  );
  return { cancelled, skipped };
}

// ─── Fulfillment transitions (PRD.305) ───

/**
 * Accept a clinician's request as in-house work. This is what puts the order
 * into the Laboratory Flow queue — creating it never did.
 */
export async function acceptLabRequest(params: {
  db: DbLike;
  orderId: string;
  actor: LabActor;
}): Promise<void> {
  const { db, orderId, actor } = params;
  const rows = (await db.getAll(`SELECT fulfillment FROM lab_orders WHERE id = ? LIMIT 1`, [
    orderId,
  ])) as { fulfillment: string | null }[];
  if (rows.length === 0) throw new Error('Laboratory order not found.');

  const verdict = canAcceptLabRequest({ fulfillment: rows[0].fulfillment });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This request cannot be accepted.');

  const now = new Date().toISOString();
  await db.execute(
    `UPDATE lab_orders
        SET fulfillment = 'in_house', fulfillment_at = ?, fulfillment_by = ?, updated_at = ?
      WHERE id = ?`,
    [now, actor.uid, now, orderId],
  );
}

/**
 * Record that the patient had the work done elsewhere. The request stays in the
 * clinical record — it is simply never queued as in-house work.
 */
export async function markLabRequestExternal(params: {
  db: DbLike;
  orderId: string;
  actor: LabActor;
}): Promise<void> {
  const { db, orderId, actor } = params;
  const rows = (await db.getAll(`SELECT fulfillment FROM lab_orders WHERE id = ? LIMIT 1`, [
    orderId,
  ])) as { fulfillment: string | null }[];
  if (rows.length === 0) throw new Error('Laboratory order not found.');

  const verdict = canMarkLabRequestExternal({ fulfillment: rows[0].fulfillment });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This request cannot be marked external.');

  const now = new Date().toISOString();
  await db.execute(
    `UPDATE lab_orders
        SET fulfillment = 'external', fulfillment_at = ?, fulfillment_by = ?, updated_at = ?
      WHERE id = ?`,
    [now, actor.uid, now, orderId],
  );
}

// ─── Results & artifacts ───

/** An already-uploaded artifact: the Storage upload happened, this links it. */
export interface UploadedLabArtifact {
  /** Pre-generated id used for BOTH the Storage folder and `patient_files.id`. */
  fileId: string;
  filename: string;
  url: string;
  mimeType: string | null;
  sizeBytes?: number | null;
  kind?: 'image' | 'document';
}

export interface AttachLabResultFilesParams {
  db: DbLike;
  orgId: string;
  clinicId?: string | null;
  patientId: string;
  testId: string;
  /** Existing DRAFT result to append to; omit to open a new one. */
  resultId?: string | null;
  encounterId?: string | null;
  visitId?: string | null;
  artifacts: UploadedLabArtifact[];
  actor: LabActor;
  summary?: string | null;
}

/**
 * Record uploaded artifacts against a test.
 *
 * Each artifact becomes an ORDINARY `patient_files` row tagged `lab` — the same
 * row the Files workspace lists — plus a `lab_result_files` link. That is what
 * makes a result one physical upload visible from both Labs and Files.
 *
 * Uploading deliberately does NOT publish and does NOT change the test's
 * status (PRD §6.2): readiness and publication are separate, explicit acts.
 */
export async function attachLabResultFiles(
  params: AttachLabResultFilesParams,
): Promise<{ resultId: string; fileIds: string[] }> {
  const { db, orgId, patientId, testId, artifacts, actor } = params;
  if (artifacts.length === 0) throw new Error('No files to attach.');

  const testRows = (await db.getAll(`SELECT status FROM lab_order_tests WHERE id = ? LIMIT 1`, [
    testId,
  ])) as { status: string | null }[];
  if (testRows.length === 0) throw new Error('Laboratory test not found.');
  const verdict = canUploadLabResult({ status: testRows[0].status });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'Files cannot be added to this test.');

  const now = new Date().toISOString();

  // Reuse the caller's draft result, else the test's newest unpublished one,
  // else open a new draft. Never appends to a published result.
  let resultId = params.resultId ?? null;
  if (resultId) {
    const published = (await db.getAll(
      `SELECT published_at FROM lab_results WHERE id = ? LIMIT 1`,
      [resultId],
    )) as { published_at: string | null }[];
    if (published[0]?.published_at != null) {
      throw new Error('That result is published. Publish a correction instead.');
    }
  } else {
    const draft = (await db.getAll(
      `SELECT id FROM lab_results
        WHERE lab_order_test_id = ? AND published_at IS NULL
        ORDER BY created_at DESC LIMIT 1`,
      [testId],
    )) as { id: string }[];
    resultId = draft[0]?.id ?? null;
  }

  if (!resultId) {
    resultId = crypto.randomUUID();
    await db.execute(
      `INSERT INTO lab_results
        (id, org_id, lab_order_test_id, patient_id, source, form_instance_id, summary,
         result_ready_at, result_ready_by, published_at, published_by, published_by_name,
         corrects_id, correction_reason, superseded_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, 'file_upload', NULL, ?,
               NULL, NULL, NULL, NULL, NULL,
               NULL, NULL, NULL, ?, ?)`,
      [resultId, orgId, testId, patientId, params.summary ?? null, now, now],
    );
  } else if (params.summary != null) {
    await db.execute(`UPDATE lab_results SET summary = ?, updated_at = ? WHERE id = ?`, [
      params.summary,
      now,
      resultId,
    ]);
  }

  const countRows = (await db.getAll(
    `SELECT COUNT(*) AS c FROM lab_result_files WHERE lab_result_id = ?`,
    [resultId],
  )) as { c: number }[];
  let sortOrder = Number(countRows[0]?.c ?? 0);

  const fileIds: string[] = [];
  for (const artifact of artifacts) {
    // 1. The artifact itself — a normal patient file, discoverable in Files.
    await db.execute(
      `INSERT INTO patient_files
        (id, org_id, clinic_id, patient_id, appointment_id, queue_id, encounter_id, visit_id,
         kind, filename, url, tag, mime_type, description, starred,
         record_date, size_bytes, uploaded_by, created_at, updated_at)
       VALUES (?, ?, ?, ?, NULL, NULL, ?, ?, ?, ?, ?, 'lab', ?, NULL, 0, ?, ?, ?, ?, ?)`,
      [
        artifact.fileId,
        orgId,
        params.clinicId ?? null,
        patientId,
        params.encounterId ?? null,
        params.visitId ?? null,
        artifact.kind ?? 'document',
        artifact.filename,
        artifact.url,
        artifact.mimeType,
        now.slice(0, 10),
        artifact.sizeBytes ?? null,
        actor.name ?? actor.uid,
        now,
        now,
      ],
    );

    // 2. The link that makes it a laboratory result artifact.
    await db.execute(
      `INSERT INTO lab_result_files
        (id, org_id, lab_result_id, patient_file_id, patient_id, sort_order,
         uploaded_by, uploaded_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        crypto.randomUUID(),
        orgId,
        resultId,
        artifact.fileId,
        patientId,
        sortOrder,
        actor.uid,
        now,
        now,
        now,
      ],
    );
    sortOrder += 1;
    fileIds.push(artifact.fileId);
  }

  return { resultId, fileIds };
}

/**
 * Publish a result (PRD §6.2 step 3).
 *
 * Explicit and separate from upload. On success the result is stamped and its
 * test moves to `published` — the ONE path that reaches that status, so a
 * published test always has a published result behind it.
 */
export async function publishLabResult(params: {
  db: DbLike;
  resultId: string;
  actor: LabActor;
}): Promise<void> {
  const { db, resultId, actor } = params;

  const rows = (await db.getAll(
    `SELECT r.id, r.lab_order_test_id, r.published_at, r.source, t.status AS test_status,
            (SELECT COUNT(*) FROM lab_result_files f WHERE f.lab_result_id = r.id) AS file_count
       FROM lab_results r
       LEFT JOIN lab_order_tests t ON t.id = r.lab_order_test_id
      WHERE r.id = ? LIMIT 1`,
    [resultId],
  )) as {
    lab_order_test_id: string | null;
    published_at: string | null;
    source: string | null;
    test_status: string | null;
    file_count: number | null;
  }[];
  if (rows.length === 0) throw new Error('Result not found.');
  const row = rows[0];

  const verdict = canPublishLabResult({
    test: { status: row.test_status },
    result: { published_at: row.published_at, source: row.source },
    attachedFileCount: Number(row.file_count ?? 0),
  });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This result cannot be published.');

  const now = new Date().toISOString();
  await db.execute(
    `UPDATE lab_results
        SET published_at = ?, published_by = ?, published_by_name = ?, updated_at = ?
      WHERE id = ? AND published_at IS NULL`,
    [now, actor.uid, actor.name ?? null, now, resultId],
  );
  if (row.lab_order_test_id) {
    await db.execute(
      `UPDATE lab_order_tests
          SET status = 'published', published_at = ?, published_by = ?, updated_at = ?
        WHERE id = ?`,
      [now, actor.uid, now, row.lab_order_test_id],
    );
  }
}

/** Mark a test Result Ready — the readiness step publication requires. */
export async function markLabTestResultReady(params: {
  db: DbLike;
  testId: string;
  actor: LabActor;
}): Promise<void> {
  await advanceLabTestStatus({ ...params, to: 'result_ready' });
}

/**
 * Open a correction for a published result (PRD §6.3).
 *
 * The published artifact is never touched: this creates a NEW draft result
 * pointing back at it via `corrects_id` and stamps the original
 * `superseded_at`. The original stays visible, still linked to its files, so
 * the clinical record remains append-only. The test is reopened to
 * `result_ready` so the correction can flow through the normal publish guard.
 */
export async function correctLabResult(params: {
  db: DbLike;
  orgId: string;
  resultId: string;
  /**
   * Kept for call-site symmetry with the other workflow functions. The
   * correction is created as a DRAFT, and drafts carry no actor stamp — the
   * acting user is recorded when the correction is published.
   */
  actor?: LabActor;
  reason?: string | null;
}): Promise<{ correctionResultId: string }> {
  const { db, orgId, resultId } = params;

  const rows = (await db.getAll(
    `SELECT id, lab_order_test_id, patient_id, published_at, superseded_at
       FROM lab_results WHERE id = ? LIMIT 1`,
    [resultId],
  )) as {
    lab_order_test_id: string | null;
    patient_id: string | null;
    published_at: string | null;
    superseded_at: string | null;
  }[];
  if (rows.length === 0) throw new Error('Result not found.');
  const original = rows[0];

  const verdict = canCorrectLabResult({
    published_at: original.published_at,
    superseded_at: original.superseded_at,
  });
  if (!verdict.ok) throw new Error(verdict.reason ?? 'This result cannot be corrected.');

  const now = new Date().toISOString();
  const correctionResultId = crypto.randomUUID();

  await db.execute(
    `INSERT INTO lab_results
      (id, org_id, lab_order_test_id, patient_id, source, form_instance_id, summary,
       result_ready_at, result_ready_by, published_at, published_by, published_by_name,
       corrects_id, correction_reason, superseded_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, 'file_upload', NULL, NULL,
             NULL, NULL, NULL, NULL, NULL,
             ?, ?, NULL, ?, ?)`,
    [
      correctionResultId,
      orgId,
      original.lab_order_test_id,
      original.patient_id,
      resultId,
      params.reason ?? null,
      now,
      now,
    ],
  );

  // `superseded_at` is the one column syncWrite still accepts on a published
  // result — see the lab_results beforeWrite hook.
  await db.execute(`UPDATE lab_results SET superseded_at = ? WHERE id = ?`, [now, resultId]);

  if (original.lab_order_test_id) {
    await db.execute(
      `UPDATE lab_order_tests SET status = 'result_ready', updated_at = ? WHERE id = ?`,
      [now, original.lab_order_test_id],
    );
  }

  return { correctionResultId };
}

// ─── Billing (PRD §11) ───

/** Map a persisted invoice_items row into the shape `invoiceSummary` expects. */
function toSummaryItem(r: {
  id: string;
  service_name: string | null;
  qty: number | string | null;
  unit_price: number | string | null;
  discount: number | string | null;
  discount_type: string | null;
  tax_rate: number | string | null;
}): InvoiceItem {
  return {
    id: r.id,
    serviceName: r.service_name ?? '',
    qty: Number(r.qty ?? 1),
    unitPrice: Number(r.unit_price ?? 0),
    discount: Number(r.discount ?? 0),
    discountType: (r.discount_type as DiscountType) ?? 'fixed',
    taxRate: Number(r.tax_rate ?? 0),
  };
}

export interface BillLabTestsParams {
  db: DbLike;
  orgId: string;
  patientId: string;
  patientName: string;
  testIds: string[];
  encounterId?: string | null;
  visitId?: string | null;
  /** Invoice date (YYYY-MM-DD). */
  date: string;
}

/**
 * Add ordered tests to the patient's invoice through the EXISTING billing
 * subsystem — same tables, same totals helper, same settlement recompute as
 * Billing and PRD.288. No laboratory-specific invoice or payment table.
 *
 * Line ids are deterministic (`lab-<testId>`), so re-running this after a
 * partial failure, or two devices billing the same order, converge on one line
 * via `ON CONFLICT (id) DO UPDATE` rather than duplicating the charge.
 */
export async function billLabTests(
  params: BillLabTestsParams,
): Promise<{ invoiceId: string; linesAdded: number }> {
  const { db, orgId, patientId, patientName, testIds } = params;
  if (testIds.length === 0) return { invoiceId: '', linesAdded: 0 };

  const now = new Date().toISOString();
  const placeholders = testIds.map(() => '?').join(', ');
  const tests = (await db.getAll(
    `SELECT id, service_id, test_name, price_cents_snapshot, status
       FROM lab_order_tests WHERE id IN (${placeholders})`,
    testIds,
  )) as {
    id: string;
    service_id: string | null;
    test_name: string | null;
    price_cents_snapshot: number | null;
    status: string | null;
  }[];

  const billable = tests.filter((t) => normalizeLabTestStatus(t.status) !== 'cancelled');
  if (billable.length === 0) return { invoiceId: '', linesAdded: 0 };

  // Reuse an open invoice for the visit/encounter, else create one — the same
  // reuse rule the initial-fee service applies.
  const scopeSql = params.encounterId
    ? `SELECT id FROM invoices WHERE patient_id = ? AND encounter_id = ? AND status IN ('draft','issued') ORDER BY created_at DESC LIMIT 1`
    : `SELECT id FROM invoices WHERE patient_id = ? AND status IN ('draft','issued') ORDER BY created_at DESC LIMIT 1`;
  const scopeParams = params.encounterId ? [patientId, params.encounterId] : [patientId];
  const openRows = (await db.getAll(scopeSql, scopeParams)) as { id: string }[];

  let invoiceId = openRows[0]?.id ?? null;
  if (!invoiceId) {
    invoiceId = crypto.randomUUID();
    const maxRows = (await db.getAll(`SELECT invoice_no FROM invoices WHERE org_id = ?`, [
      orgId,
    ])) as { invoice_no: string | null }[];
    const maxNum = maxRows.reduce((max: number, row) => {
      const n = parseInt((row.invoice_no ?? '').replace('INV-', ''), 10);
      return Number.isFinite(n) && n > max ? n : max;
    }, 0);
    await db.execute(
      `INSERT INTO invoices
        (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
         invoice_no, patient_name, date_iso, status, notes,
         subtotal, discount_total, tax_total, grand_total, created_at, updated_at)
       VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, 'draft', '', 0, 0, 0, 0, ?, ?)`,
      [
        invoiceId,
        orgId,
        patientId,
        params.encounterId ?? null,
        params.visitId ?? null,
        `INV-${String(maxNum + 1).padStart(5, '0')}`,
        patientName,
        params.date,
        now,
        now,
      ],
    );
  }

  const countRows = (await db.getAll(
    `SELECT COUNT(*) AS c FROM invoice_items WHERE invoice_id = ?`,
    [invoiceId],
  )) as { c: number }[];
  let sortOrder = Number(countRows[0]?.c ?? 0);

  let linesAdded = 0;
  for (const test of billable) {
    const itemId = labInvoiceItemId(test.id);
    const already = (await db.getAll(`SELECT id FROM invoice_items WHERE id = ? LIMIT 1`, [
      itemId,
    ])) as { id: string }[];
    if (already.length > 0) continue; // deterministic id ⇒ already billed

    const cents = Number(test.price_cents_snapshot ?? 0);
    await db.execute(
      `INSERT INTO invoice_items
        (id, org_id, invoice_id, service_name, service_id, qty, unit_price, discount, discount_type, tax_rate,
         inventory_item_id, free_text_name,
         description_snapshot, sku_snapshot, unit_snapshot, unit_price_snapshot,
         tax_inclusive_snapshot, open_pricing_snapshot, source_type, source_id,
         remarks, add_to_treatment, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, 1, ?, 0, 'fixed', 0,
               NULL, NULL,
               NULL, NULL, NULL, NULL,
               NULL, NULL, ?, ?,
               NULL, 0, ?, ?, ?)`,
      [
        itemId,
        orgId,
        invoiceId,
        test.test_name ?? 'Laboratory test',
        test.service_id,
        Number.isFinite(cents) ? cents / 100 : 0,
        LAB_TEST_INVOICE_SOURCE_TYPE,
        test.id,
        sortOrder,
        now,
        now,
      ],
    );
    sortOrder += 1;
    linesAdded += 1;
  }

  // Totals + settlement through the shared helpers, exactly as Billing does.
  const itemRows = await db.getAll(
    `SELECT id, service_name, qty, unit_price, discount, discount_type, tax_rate
       FROM invoice_items WHERE invoice_id = ?`,
    [invoiceId],
  );
  const sum = invoiceSummary(itemRows.map(toSummaryItem));
  await db.execute(
    `UPDATE invoices
        SET subtotal = ?, discount_total = ?, tax_total = ?, grand_total = ?, updated_at = ?
      WHERE id = ?`,
    [sum.subtotal, sum.discountTotal, sum.taxTotal, sum.grandTotal, now, invoiceId],
  );
  if (sum.grandTotal > 0) {
    await db.execute(
      `UPDATE invoices SET status = 'issued', updated_at = ? WHERE id = ? AND status = 'draft'`,
      [now, invoiceId],
    );
  }
  await recomputeInvoiceItemPaymentStatus(db, invoiceId);

  return { invoiceId, linesAdded };
}
